import type { CSSProperties, ReactNode } from "react";
import {
  isAccepting,
  questRanks,
  type JobQuest,
  type QuestRank,
} from "../types";

export function QuestStatus({ quest }: { quest: JobQuest }) {
  const open = isAccepting(quest);
  return (
    <span className={`jq-status ${open ? "is-accepting" : "is-closed"}`}>
      <span className="jq-status-dot" aria-hidden="true" />
      {open ? "ACCEPTING / 募集中" : "CLOSED / 募集終了"}
    </span>
  );
}

export function RankBadge({ rank }: { rank: QuestRank }) {
  return (
    <div
      className="jq-rank"
      title={questRanks[rank]}
      aria-label={`Quest難易度 ${rank}：${questRanks[rank]}`}
    >
      <span>QUEST RANK</span>
      <strong>{rank}</strong>
    </div>
  );
}

export function SkillTags({ skills }: { skills: string[] }) {
  return (
    <ul className="jq-tags" aria-label="スキル">
      {skills.map((skill) => (
        <li key={skill}>{skill}</li>
      ))}
    </ul>
  );
}

export function QuestBalance({
  quest,
  detailed = false,
}: {
  quest: JobQuest;
  detailed?: boolean;
}) {
  if (!quest.selectionWeightsConfirmed) {
    return (
      <div className={`jq-balance is-pending${detailed ? " is-detailed" : ""}`}>
        <span className="jq-field-label">PERSONALITY / SKILL</span>
        <strong>選考比率は未公表</strong>
        {detailed && (
          <p>
            企業の選考方針が確認でき次第、比率を表示します。AIYSSによる応募者の採点ではありません。
          </p>
        )}
      </div>
    );
  }
  return (
    <div className={`jq-balance${detailed ? " is-detailed" : ""}`}>
      <div className="jq-balance-labels">
        <span>
          Personality <b>{quest.personalityWeight}%</b>
        </span>
        <span>
          Skill <b>{quest.skillWeight}%</b>
        </span>
      </div>
      <div
        className="jq-balance-track"
        role="img"
        aria-label={`企業の選考方針：人柄・姿勢${quest.personalityWeight}%、スキル${quest.skillWeight}%`}
      >
        <span
          style={
            { "--personality": `${quest.personalityWeight}%` } as CSSProperties
          }
        />
      </div>
      {detailed && (
        <p>
          Personalityは人柄・取り組む姿勢、Skillは技術・経験。比率はこのQuestで企業が重視する選考方針です。AIYSSによる応募者の採点ではありません。
        </p>
      )}
    </div>
  );
}

export function QuestDisclaimer() {
  return (
    <aside className="jq-disclaimer" aria-label="サービスについて">
      <span className="jq-eyebrow">REAL WORK. YOUR CHOICE.</span>
      <p>
        AIYSS Job
        Questに掲載されているQuestは、企業等から提供される実際の業務委託案件です。
      </p>
      <p>
        応募者自身がQuestを選択して応募し、選考は各企業が行います。AIYSSは応募者の推薦・評価・選考・採用判断を行いません。
      </p>
    </aside>
  );
}

export function QuestFlow({ steps }: { steps: string[] }) {
  return (
    <ol className="jq-flow">
      {steps.map((step, index) => (
        <li key={`${index}-${step}`}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <strong>{step}</strong>
        </li>
      ))}
    </ol>
  );
}

export function QuestSection({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="jq-detail-section">
      <p className="jq-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export function QuestLoadState({
  status,
  message,
  retry,
}: {
  status: string;
  message: string;
  retry: () => void;
}) {
  if (status === "ready") return null;
  return (
    <div className="jq-empty" role={status === "error" ? "alert" : "status"}>
      <p className="jq-eyebrow">
        {status === "missing"
          ? "QUEST NOT FOUND"
          : status === "error"
            ? "CONNECTION ERROR"
            : "LOADING QUESTS"}
      </p>
      <p>
        {status === "loading"
          ? "Questを読み込んでいます…"
          : status === "missing"
            ? "このQuestは見つからないか、現在公開されていません。"
            : message}
      </p>
      {status === "error" && (
        <button className="jq-button is-secondary" onClick={retry}>
          再読み込み
        </button>
      )}
      {status === "missing" && (
        <a className="jq-button" href="/job-quest/board/">
          Quest Boardへ戻る →
        </a>
      )}
    </div>
  );
}
